// resources/js/src/components/community/components/profile/FriendsGrid.tsx
import { Link } from "react-router";
import { Icon } from "@iconify/react";
import { FriendsGridProps } from "../interface/CommunityProfile";

type Props = FriendsGridProps & {
  onSeeAll?: () => void;
};

export default function FriendsGrid({
  friends,
  title = "Friends",
  showSeeAll = true,
  onSeeAll,
}: Props) {
  const displayFriends = friends.slice(0, 9);

  return (
    <div className="bg-white dark:bg-deepSlate rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 transition-shadow duration-300 hover:shadow-md">
      {/* ===== Header ===== */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon
            icon="mdi:account-group-outline"
            className="text-primary-600 dark:text-primary-400 text-xl"
          />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {title}
          </h2>
        </div>
        {showSeeAll && (
          <button
            onClick={onSeeAll}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 px-2.5 py-1.5 rounded-lg transition-all duration-200 active:scale-[0.97]"
          >
            See All
            <Icon icon="mdi:chevron-right" className="text-base" />
          </button>
        )}
      </div>

      {/* ===== Grid ===== */}
      {displayFriends.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {displayFriends.map((friend) => (
            <Link
              key={friend.id}
              to={`/community/profile/${friend.id}`}
              className="flex flex-col items-center text-center group rounded-xl p-2 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
            >
              {/* Avatar */}
              <div className="w-full aspect-square rounded-full overflow-hidden mb-2 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 ring-2 ring-transparent group-hover:ring-primary-500 transition-all duration-300 shadow-sm group-hover:shadow-md">
                {friend.avatarUrl ? (
                  <img
                    src={friend.avatarUrl}
                    alt={friend.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg font-bold text-gray-400 dark:text-gray-500">
                    {friend.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Name */}
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate w-full">
                {friend.name}
              </h3>

              {/* Mutual Friends */}
              <p className="text-gray-500 dark:text-gray-400 text-xs truncate w-full">
                {friend.mutualFriends > 0
                  ? `${friend.mutualFriends} mutual`
                  : "\u00A0"}
              </p>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-4">
          No friends to show yet.
        </p>
      )}
    </div>
  );
}